/**
 * 🔗 useAffiliateTracking Hook
 * 
 * Client-side affiliate tracking:
 * - Detects ref cookie from the server (set by /ref/:code redirects)
 * - Listens to analytics events and forwards affiliate-relevant ones as conversions
 * - Provides ref code info to components that need it
 * 
 * This hook should be mounted once in the App root.
 */

import { useEffect, useRef, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface AffiliateRef {
  /** The affiliate ref code, or null if no ref is active */
  refCode: string | null;
  /** Whether we've checked for a ref yet */
  isLoaded: boolean;
}

// Events that count as affiliate conversions
const AFFILIATE_CONVERSION_EVENTS = [
  'restaurant_profile_view',
  'click_call',
  'click_route',
  'click_menu',
  'click_website',
  'listing_card_click',
] as const;

// ============================================
// HOOK: useAffiliateRef
// ============================================

/**
 * Fetches the current ref code from the server cookie.
 * Returns { refCode, isLoaded }
 */
export function useAffiliateRef(): AffiliateRef {
  const [ref, setRef] = useState<AffiliateRef>({ refCode: null, isLoaded: false });

  useEffect(() => {
    fetch('/api/affiliate/ref-info')
      .then((res) => res.json())
      .then((data) => {
        setRef({ refCode: data.ref || null, isLoaded: true });
      })
      .catch(() => {
        setRef({ refCode: null, isLoaded: true });
      });
  }, []);

  return ref;
}

// ============================================
// HOOK: useAffiliateConversionTracker
// ============================================

/**
 * Hooks into the existing analytics event pipeline to also track
 * affiliate conversions for users who arrived via a ref link.
 * 
 * Usage: Mount once in App component.
 * ```tsx
 * function App() {
 *   useAffiliateConversionTracker();
 *   return <Router>...</Router>;
 * }
 * ```
 */
export function useAffiliateConversionTracker(): void {
  const refChecked = useRef(false);
  const refCode = useRef<string | null>(null);
  const sessionId = useRef<string | null>(null);
  const trackedEvents = useRef(new Set<string>());

  // Check for ref cookie on mount
  useEffect(() => {
    if (refChecked.current) return;
    refChecked.current = true;

    fetch('/api/affiliate/ref-info')
      .then((res) => res.json())
      .then((data) => {
        refCode.current = data.ref || null;
      })
      .catch(() => {
        refCode.current = null;
      });

    // Get or create session ID (same as analytics uses)
    sessionId.current = getOrCreateSessionId();
  }, []);

  // Intercept analytics event submissions to also track as affiliate conversions
  useEffect(() => {
    // Monkey-patch the global analytics tracker to also send conversions
    // Listen for custom events dispatched by the analytics system
    const handleAnalyticsEvent = (event: CustomEvent) => {
      if (!refCode.current) return;

      const { event_name, restaurant_id, session_id: evtSessionId } = event.detail || {};
      if (!event_name) return;

      // Only track conversion-relevant events
      if (!AFFILIATE_CONVERSION_EVENTS.includes(event_name)) return;

      // Deduplicate: don't track the same event+restaurant combo twice in this session
      const dedupeKey = `${event_name}:${restaurant_id || 'none'}`;
      if (trackedEvents.current.has(dedupeKey)) return;
      trackedEvents.current.add(dedupeKey);

      // Send conversion event to the affiliate tracking endpoint
      fetch('/api/affiliate/track-conversion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: event_name,
          restaurant_id: restaurant_id || null,
          session_id: evtSessionId || sessionId.current,
          value: 0,
          metadata: {
            path: window.location.pathname,
            referrer: document.referrer || null,
          },
        }),
      }).catch(() => {
        // Silently fail — don't impact UX
      });
    };

    window.addEventListener('sg:analytics' as any, handleAnalyticsEvent as EventListener);
    return () => {
      window.removeEventListener('sg:analytics' as any, handleAnalyticsEvent as EventListener);
    };
  }, []);
}

// ============================================
// HELPERS
// ============================================

/**
 * Get or create a session ID (mirrors the analytics session ID logic)
 */
function getOrCreateSessionId(): string {
  const key = 'sg_session_id';
  let sid = sessionStorage.getItem(key);
  if (!sid) {
    sid = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    sessionStorage.setItem(key, sid);
  }
  return sid;
}

/**
 * Dispatch an analytics event that the affiliate tracker can listen to.
 * Call this from your analytics event sending code.
 */
export function dispatchAnalyticsEvent(eventName: string, restaurantId?: string | null, sessionId?: string): void {
  const event = new CustomEvent('sg:analytics', {
    detail: {
      event_name: eventName,
      restaurant_id: restaurantId || null,
      session_id: sessionId || getOrCreateSessionId(),
    },
  });
  window.dispatchEvent(event);
}
